import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash2 } from "lucide-react";
import axios from "axios";
import { formatApiError, API, authHeaders } from "@/hooks/useAdminData";

export default function EventsManager({ events, loadData }) {
  const [eventOpen, setEventOpen] = useState(false);
  const [eventForm, setEventForm] = useState({ title: "", description: "", date: "", time: "", location: "" });

  const addEvent = async () => {
    try {
      await axios.post(`${API}/events`, eventForm, { withCredentials: true, headers: authHeaders() });
      setEventForm({ title: "", description: "", date: "", time: "", location: "" });
      setEventOpen(false);
      loadData();
    } catch (err) {
      alert(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteEvent = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await axios.delete(`${API}/events/${id}`, { withCredentials: true, headers: authHeaders() }).catch(() => {});
    loadData();
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-[#1A1A1A]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Events ({events.length})</h3>
        <Dialog open={eventOpen} onOpenChange={setEventOpen}>
          <DialogTrigger asChild>
            <Button className="btn-secondary text-sm" data-testid="add-event-btn"><Plus size={14} className="mr-1" /> Add Event</Button>
          </DialogTrigger>
          <DialogContent className="rounded-2xl">
            <DialogHeader><DialogTitle>Add Event</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div>
                <Label className="text-sm">Title</Label>
                <Input value={eventForm.title} onChange={e => setEventForm({...eventForm, title: e.target.value})} className="rounded-xl mt-1" data-testid="event-title-input" />
              </div>
              <div>
                <Label className="text-sm">Date</Label>
                <Input type="date" value={eventForm.date} onChange={e => setEventForm({...eventForm, date: e.target.value})} className="rounded-xl mt-1" data-testid="event-date-input" />
              </div>
              <div>
                <Label className="text-sm">Time</Label>
                <Input value={eventForm.time} onChange={e => setEventForm({...eventForm, time: e.target.value})} placeholder="e.g. 10:00 AM" className="rounded-xl mt-1" data-testid="event-time-input" />
              </div>
              <div>
                <Label className="text-sm">Location</Label>
                <Input value={eventForm.location} onChange={e => setEventForm({...eventForm, location: e.target.value})} className="rounded-xl mt-1" data-testid="event-location-input" />
              </div>
              <div>
                <Label className="text-sm">Description</Label>
                <Textarea value={eventForm.description} onChange={e => setEventForm({...eventForm, description: e.target.value})} className="rounded-xl mt-1" data-testid="event-desc-input" />
              </div>
              <Button onClick={addEvent} className="btn-primary w-full" data-testid="event-submit-btn">Add Event</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow><TableHead>Title</TableHead><TableHead>Date</TableHead><TableHead>Time</TableHead><TableHead>Location</TableHead><TableHead className="w-[80px]">Actions</TableHead></TableRow>
          </TableHeader>
          <TableBody>
            {events.map(event => (
              <TableRow key={event.id}>
                <TableCell className="font-medium">{event.title}</TableCell>
                <TableCell>{event.date}</TableCell>
                <TableCell>{event.time}</TableCell>
                <TableCell>{event.location}</TableCell>
                <TableCell>
                  <button onClick={() => deleteEvent(event.id)} className="text-red-500 hover:text-red-700 p-1" data-testid={`delete-event-${event.id}`}>
                    <Trash2 size={16} />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {events.length === 0 && <p className="text-center text-gray-400 py-8 text-sm">No events yet.</p>}
      </div>
    </>
  );
}
